<script setup>
import { computed, ref } from 'vue';
import AppMenuItem from './AppMenuItem.vue';
import { isReadOnlyImpersonation } from '@/util/apiClient';
import { stores } from '@/util/stores.js';
import { RoleGroups } from '@/router/roles.ts';

// Routes that should be hidden during restricted admin impersonation.
// Today the sidebar only exposes finance surfaces, but keep profile/settings here
// so future nav changes inherit the restriction automatically.
const RESTRICTED_IMPERSONATION_HIDDEN_ROUTES = ['/app/financial', '/app/assignor-financial', '/app/profile', '/app/settings'];
const hasRefAdminRole = computed(() => stores.usersStore.hasRole(['Referee Administrator']));
const hasAssignorRole = computed(() => stores.usersStore.hasRole(['Referee Assignor']));

const model = ref([
	{
		label: 'Evergreen Sports',
		roles: ['Evergreen Sports'],
		items: [{ label: 'Dashboard', icon: ['fas', 'house'], to: '/app/employee', roles: ['Evergreen Sports'] }]
	},
	{
		label: 'Administrator',
		roles: ['Referee Administrator'],
		items: [
			{ label: 'Dashboard', icon: ['fas', 'house'], to: '/app/admin', roles: ['Referee Administrator'] },
			{ label: 'Locations', icon: ['fas', 'map-location-dot'], to: '/app/locations', roles: ['Referee Administrator'] },
			{
				label: 'Finance',
				icon: ['fas', 'dollar-sign'],
				roles: ['Referee Administrator'],
				items: [
					{ label: 'Overview', icon: ['fas', 'chart-line'], to: '/app/admin/finance/home', roles: ['Referee Administrator'] },
					{ label: 'Review', icon: ['fas', 'circle-check'], to: '/app/admin/finance/review', roles: ['Referee Administrator'] },
					{ label: 'Invoices', icon: ['fas', 'file-invoice-dollar'], to: '/app/admin/finance/invoices', roles: ['Referee Administrator'] },
					{ label: 'Payouts', icon: ['fas', 'paper-plane'], to: '/app/admin/finance/payouts', roles: ['Referee Administrator'] },
					{ label: 'Activity', icon: ['fas', 'clock-rotate-left'], to: '/app/admin/finance/ledger', roles: ['Referee Administrator'] },
					{ label: 'Reports', icon: ['fas', 'chart-column'], to: '/app/admin/finance/reports', roles: ['Referee Administrator'] },
					{ label: 'Settings', icon: ['fas', 'gear'], to: '/app/admin/finance/settings', roles: ['Referee Administrator'] }
				]
			}
		]
	},
	{
		label: 'Assignor',
		roles: ['Referee Assignor'],
		items: [
			{ label: 'Dashboard', icon: ['fas', 'house'], to: '/app/assignor', roles: ['Referee Assignor'] },
			{ label: 'Upload Settings', icon: ['fas', 'gears'], to: '/app/upload-settings', roles: ['Referee Assignor'] },
			{ label: 'Fees', icon: ['fas', 'money-bill-wave'], to: '/app/fees', roles: ['Referee Assignor'] },
			{ label: 'Locations', icon: ['fas', 'map-location-dot'], to: '/app/locations', roles: ['Referee Assignor'] },
			{ label: 'Match Reports', icon: ['fas', 'file-lines'], to: '/app/assignor-reports', roles: ['Referee Assignor'] },
			{ label: 'My Pay', icon: ['fas', 'dollar'], to: '/app/assignor-financial', roles: ['Referee Assignor'] }
			// { label: 'Broadcast', icon: ['fas', 'bullhorn'], to: '/broadcast', roles: ['Referee Assignor'] },
		]
	},
	{
		label: 'Treasurer (Coming Soon)',
		roles: ['TREASURER'],
		items: [
			// { label: 'Dashboard', icon: ['fas', 'house'], to: '/treasurer', roles: ['TREASURER'] },
			// { label: 'Client Contact', icon: 'fw mobile-screen', to: '/', roles: ['TREASURER'] }
		]
	},
	{
		label: 'Official',
		roles: ['Official'],
		items: [
			{ label: 'Dashboard', icon: ['fas', 'house'], to: '/app/officials', roles: ['Official'] },
			{ label: 'Reports', icon: ['fas', 'file-lines'], to: '/app/my-reports', roles: ['Official'] },
			{ label: 'My Pay', icon: ['fas', 'dollar'], to: '/app/financial', roles: ['Official'] }
			// { label: 'Contacts List', icon: ['fas', 'address-book'], to: '/app/contacts', roles: ['Official'] }
			// { label: 'Documents', icon: ['fas', 'book'], to: '/documents', roles: ['Official'] }
		]
	},
	{
		label: 'Club Administrator',
		roles: ['Club Administrator'],
		items: [
			{ label: 'Dashboard', icon: ['fas', 'house'], to: '/app/client', roles: ['Club Administrator'] },
			{ label: 'Billing', icon: ['fas', 'file-invoice-dollar'], to: '/app/client/invoices', roles: ['Club Administrator'] },
			{ label: 'Games', icon: ['fas', 'calendar-days'], to: '/app/client/games', roles: ['Club Administrator'] },
			{ label: 'Support', icon: ['fas', 'headset'], to: '/app/support', roles: ['Club Administrator'] }
		]
	},
	{
		label: 'Club Scheduler',
		roles: ['Club Scheduler'],
		hideWhenAnyRole: ['Club Administrator'],
		items: [
			{ label: 'Dashboard', icon: ['fas', 'house'], to: '/app/client', roles: ['Club Scheduler'] },
			{ label: 'Games', icon: ['fas', 'calendar-days'], to: '/app/client/games', roles: ['Club Scheduler'] },
			{ label: 'Support', icon: ['fas', 'headset'], to: '/app/support', roles: ['Club Scheduler'] }
		]
	},
	{
		label: 'League',
		roles: ['LEAGUE'],
		items: []
	},
	{
		label: 'Groups',
		roles: ['User'],
		items: [
			{ label: 'View All Groups', to: '/app/user', roles: ['User'], hideWhenAnyRole: RoleGroups.CLUB },
			{ label: 'Support', icon: ['fas', 'headset'], to: '/app/support', roles: ['User'], hideWhenAnyRole: RoleGroups.CLUB }
		]
	}
]);

const isHiddenDuringImpersonation = (route) => {
	return isReadOnlyImpersonation() && RESTRICTED_IMPERSONATION_HIDDEN_ROUTES.includes(route);
};

const filteredModel = computed(() => {
	return model.value
		.filter((item) => stores.usersStore.hasRole(item.roles))
		.filter((item) => !item.hideWhenAnyRole || !stores.usersStore.hasRole(item.hideWhenAnyRole))
		.map((item) => {
			const filteredItems = (item.items || []).filter((subItem) => {
				if (!stores.usersStore.hasRole(subItem.roles)) return false;
				if (subItem.hideWhenAnyRole && stores.usersStore.hasRole(subItem.hideWhenAnyRole)) return false;
				if (isHiddenDuringImpersonation(subItem.to)) return false;

				const isLocationsItem = subItem.to === '/app/locations';
				const isAssignorSection = item.label === 'Assignor';

				if (isLocationsItem && isAssignorSection && hasRefAdminRole.value && hasAssignorRole.value) {
					return false;
				}

				return true;
			});

			return {
				...item,
				items: filteredItems
			};
		})
		.filter((item) => item.items.length > 0);
});
</script>

<template>
	<ul class="m-0 list-none p-0">
		<template v-for="(item, i) in filteredModel">
			<app-menu-item v-if="!item.separator" :key="`menu-item-${i}`" :item="item" :index="i"></app-menu-item>
			<li v-if="item.separator" :key="`menu-separator-${i}`" class="my-2 border-t border-surface"></li>
		</template>
	</ul>
</template>
